// src/components/TickerSearchInput.jsx
// TICKER-SEARCH-AUTOCOMPLETE — new file.
// Generic — lives at src/components/ (not under filings/) since it's meant
// for reuse across pages, per product decision this session.

import React, { useState, useRef, useEffect } from 'react';
import { useTickerAutocomplete } from '../hooks/useTickerAutocomplete';

export default function TickerSearchInput({
  onSelect, onChange, onSubmit,
  placeholder = 'Search ticker or company…', initialValue = '', style,
}) {
  const [inputValue, setInputValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { suggestions, loading } = useTickerAutocomplete(inputValue);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (s) => {
    setInputValue(s.ticker);
    setOpen(false);
    onSelect && onSelect(s.ticker, s.name);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: '1 1 200px', ...style }}>
      <input
        type="text"
        value={inputValue}
        onChange={e => {
          const v = e.target.value.toUpperCase();
          setInputValue(v);
          setOpen(true);
          onChange && onChange(v); // keeps a parent's own state in sync, e.g. HeroSection's ticker state
        }}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        onKeyPress={e => {
          if (e.key !== 'Enter') return;
          // onSubmit (if provided) always fires with the raw typed value — matches
          // Home page's original "Enter always analyzes whatever you typed" behavior,
          // independent of whether a dropdown suggestion exists yet. Without onSubmit
          // (e.g. FilingFilters.jsx), Enter falls back to picking the top suggestion —
          // original behavior for that consumer, unchanged.
          if (onSubmit) { setOpen(false); onSubmit(inputValue); }
          else if (suggestions.length) pick(suggestions[0]);
        }}
        placeholder={placeholder}
        style={{ width: '100%', padding: '0.55rem 0.9rem', background: 'rgba(15,30,60,0.6)',
          border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', color: '#f8fafc',
          fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />

      {open && inputValue.trim().length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: 'rgba(8,18,40,0.98)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '8px',
          boxShadow: '0 12px 30px rgba(0,0,0,0.5)', maxHeight: '280px', overflowY: 'auto' }}>
          {loading && (
            <div style={{ padding: '0.6rem 0.9rem', fontSize: '0.75rem', color: '#64748b' }}>Searching…</div>
          )}
          {!loading && suggestions.length === 0 && (
            <div style={{ padding: '0.6rem 0.9rem', fontSize: '0.75rem', color: '#475569' }}>No matches</div>
          )}
          {!loading && suggestions.map(s => (
            <button key={s.ticker} onClick={() => pick(s)}
              style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
                gap: '0.75rem', padding: '0.55rem 0.9rem', background: 'none', border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#00d4ff', flex: '0 0 70px' }}>{s.ticker}</span>
              <span style={{ fontSize: '0.78rem', color: '#cbd5e1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              {s.sector && (
                <span style={{ fontSize: '0.68rem', color: '#475569', flex: '0 0 auto' }}>{s.sector}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
